import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useClient } from "../../../hooks/useClient";
import { translate, verifyHideCountryItems, getNameStatusClients } from "../../../utils/Common";
import PageContainer from '../../../components/layout/PageContainer/PageContainer';
import PageHeader from '../../../components/layout/PageHeader/PageHeader';
import ContentCard from '../../../components/layout/ContentCard/ContentCard';
import Button from '../../../components/common/Button/Button';
import Badge from '../../../components/common/Badge/Badge';
import Tabs from '../../../components/common/Tabs/Tabs';
import Spinner from '../../../components/Spinner';
import ModalContent from '../../../components/ModalContent';
import { ToastContainer, useToast } from '../../../components/common/Toast';
import * as FaIcons from 'react-icons/fa';
import { FaUserFriends } from 'react-icons/fa';
import { FaRegBuilding } from 'react-icons/fa';
import { GiPlatform } from 'react-icons/gi';
import styles from './ClientDetail.module.css';
import { verifyAccessViewPermissions } from "../../../config/permissions";

// Utils
import { getStatusVariant, formatClientName } from './utils/clientHelpers';
import { formatClientNameWithId } from './utils/clientFormatters';
import { getClientConfRoleInfo } from '../../../utils/modules/clients';

// Importar componentes de tabs
import PersonalInfoTab from './components/PersonalInfoTab';
import PlatformInfoTab from './components/PlatformInfoTab';
import CompaniesTab from './components/CompaniesTab';
// import BalanceTab from './components/BalanceTab'; // Comentado - no mostrar balance management

const DetailsClients = () => {
  const { clientId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const permissionsPath = "/dashboard/clients";
  const canRead = verifyAccessViewPermissions(["read"], { path: permissionsPath || location.pathname });
  const canUpdate = verifyAccessViewPermissions(["update"], { path: permissionsPath || location.pathname });
  const { getDetailsClient, updateDetailsClient } = useClient();
  const { toasts, showSuccess, showError, removeToast } = useToast();
  const [detailClient, setDetailClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [totalCompanies, setTotalCompanies] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  // Estatus destino del modal de confirmación (1: activar, 2: desactivar); null = cerrado
  const [pendingStatus, setPendingStatus] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const loadClient = async () => {
      setLoading(true);
      setError(null);
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      const liveController = new AbortController();
      abortControllerRef.current = liveController;
      
      try {
        const response = await getDetailsClient(abortControllerRef.current.signal, {
          clientId: clientId,
        });
        setDetailClient(response);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading client:', err);
          setError(err.message || translate('dashboard.clients.detail.errors.loadError'));
        }
      } finally {
        // Only the request that was not superseded may clear the flag: an
        // aborted one used to switch it off for its replacement, blanking the
        // view until the new data arrived.
        if (!liveController.signal.aborted) setLoading(false);
      }
    };

    if (clientId) {
      loadClient();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [clientId, getDetailsClient]);

  const handleBack = () => {
    history.push('/dashboard/clients');
  };

  const handleBreadcrumbClick = (e) => {
    e.preventDefault();
    handleBack();
  };

  const handleGetDetailsClient = async (data) => {
    const abortController = new AbortController();
    try {
      const response = await getDetailsClient(abortController.signal, data);
      setDetailClient(response);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error reloading client:', err);
        showError(err.message || translate('dashboard.clients.detail.errors.loadError'));
      }
    }
  };

  const handleUpdateClient = async (updateData) => {
    if (!detailClient) return { success: false, error: translate('dashboard.clients.detail.errors.notFound') };
    if (!canUpdate) return { success: false, error: translate("dashboard.clients.formSearch.NOT_ACCESS") };
    
    try {
      const abortController = new AbortController();
      const response = await updateDetailsClient(abortController.signal, {
        clientId: clientId,
        ...updateData
      });
      
      if (response && response.code === 200) {
        showSuccess(translate('dashboard.updateClient.success_message'));
        // Reload client data
        await handleGetDetailsClient({
          clientId: clientId,
        });
        return { success: true, result: response };
      } else {
        showError(translate('dashboard.platforms.create.alert.error'));
        return { success: false, error: response?.message || translate('dashboard.clients.detail.errors.updateError') };
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error updating client:', error);
        showError(error.message || translate('dashboard.clients.detail.errors.updateError'));
        return { success: false, error: error.message || translate('dashboard.clients.detail.errors.updateError') };
      }
      return { success: false, error: 'Request cancelled' };
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatus) return;
    setUpdatingStatus(true);
    await handleUpdateClient({
      idEstatus: pendingStatus,
      typeUpdate: "EDIT_STATUS_CLIENT",
    });
    setUpdatingStatus(false);
    setPendingStatus(null);
  };

  const formatClientData = (data) => {
    let accValue = null;
    if (Array.isArray(data.acc)) {
      accValue = data.acc.length > 0;
    } else if (typeof data.acc === "boolean") {
      accValue = data.acc;
    }
    return {
      ...data,
      acc: accValue,
    };
  };

  const statusVariant = detailClient ? getStatusVariant(detailClient?.IdEstatus) : 'neutral';
  const statusText = detailClient ? getNameStatusClients(detailClient?.IdEstatus) : '';
  // Pendiente (3) puede ir a cualquiera de los dos estados
  const canActivate = canUpdate && (detailClient?.IdEstatus === 2 || detailClient?.IdEstatus === 3);
  const canDeactivate = canUpdate && (detailClient?.IdEstatus === 1 || detailClient?.IdEstatus === 3);

  const tabs = [
    {
      label: translate('dashboard.clients.detail.tabs.personalInfo') || translate('dashboard.userprofile.personal.title'),
      icon: <FaIcons.FaUser />,
      component: (
        <PersonalInfoTab 
          client={detailClient}
          onUpdateClient={handleUpdateClient}
          formatClientData={formatClientData}
          onReloadClient={handleGetDetailsClient}
          clientId={clientId}
          canUpdate={canUpdate}
        />
      ),
      show: true,
    },
    {
      label:  translate('global.locker'),
      icon: <GiPlatform />,
      component: (
        <PlatformInfoTab 
          client={detailClient}
          onUpdateClient={handleUpdateClient}
          onReloadClient={handleGetDetailsClient}
          clientId={clientId}
          canUpdate={canUpdate}
        />
      ),
      show: true,
    },
    {
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{translate('dashboard.clients.detail.tabs.companies')}</span>
          {loadingCompanies ? (
            <Spinner type={1} size={12} />
          ) : (
            <Badge variant="neutral" size="sm" showIcon={false}>
              {totalCompanies ?? 0}
            </Badge>
          )}
        </span>
      ),
      icon: <FaRegBuilding />,
      component: (
        <CompaniesTab 
          showError={showError}
          userIdGL={detailClient?.UserIdGL}
          onCountChange={setTotalCompanies}
          onLoadingChange={setLoadingCompanies}
          canSearch={canRead}
        />
      ),
      show: true,
    }
  ].filter(tab => tab.show);

  if (loading) {
    return (
      <PageContainer>
        <div className={styles.loadingContainer}>
          <Spinner type={1} size={40} />
        </div>
      </PageContainer>
    );
  }

  if (!canRead) {
    return (
      <PageContainer>
        <PageHeader
          title={translate('global.clients')}
          icon={<FaUserFriends size={24} />}
          divider={false}
        />
        <ContentCard padding="md">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(107,114,128,0.12)",
                color: "#374151",
                flex: "0 0 auto",
              }}
              aria-hidden
            >
              <FaIcons.FaLock size={16} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>
                {translate("dashboard.configuration.noPermissionTitle")}
              </div>
              <div style={{ marginTop: 4, color: "#6B7280" }}>
                {translate("dashboard.clients.formSearch.NOT_ACCESS")}
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button variant="primary" type="button" onClick={handleBack}>
                  {translate("global.goBack")}
                </Button>
              </div>
            </div>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  if (error || !detailClient) {
    return (
      <PageContainer>
        <ContentCard padding="md">
          <div className={styles.errorContainer}>
            <p>{error || translate('dashboard.clients.detail.errors.notFound')}</p>
            <Button onClick={handleBack} variant="primary">
              {translate('dashboard.clients.detail.actions.backToClients') || translate('global.clients')}
            </Button>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={formatClientNameWithId(detailClient)}
        breadcrumbs={[
          (
            <span
              key="clients"
              onClick={handleBreadcrumbClick}
              style={{ 
                cursor: 'pointer', 
                textDecoration: 'none',
                color: 'var(--text-secondary, #86868b)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                padding: '0.25rem 0.5rem',
                margin: '-0.25rem -0.5rem',
                borderRadius: '0.375rem',
                display: 'inline-block',
                userSelect: 'none'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'var(--text-primary, #1d1d1f)';
                e.target.style.backgroundColor = 'var(--bg-hover, rgba(0, 0, 0, 0.04))';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'var(--text-secondary, #86868b)';
                e.target.style.backgroundColor = 'transparent';
              }}
              onMouseDown={(e) => {
                e.target.style.transform = 'scale(0.97)';
                e.target.style.backgroundColor = 'var(--bg-hover, rgba(0, 0, 0, 0.06))';
              }}
              onMouseUp={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              {translate('global.clients')}
            </span>
          ),
          formatClientNameWithId(detailClient)
        ]}
        subtitle={
          <div className={styles.subtitleWithBadge}>
            {detailClient?.Usuario ? (
              <Badge variant="neutral" icon={<FaIcons.FaUser size={14} />} showIcon={true}>
                {detailClient.Usuario}
              </Badge>
            ) : (
              <span>ID: {formatClientName(detailClient)}</span>
            )}
            {detailClient?.IdConfRoles && (
              <Badge 
                variant="neutral" 
                icon={getClientConfRoleInfo(detailClient.IdConfRoles).icon} 
                showIcon={true}
              >
                {getClientConfRoleInfo(detailClient.IdConfRoles).label}
              </Badge>
            )}
            <Badge variant={statusVariant} showIcon>
              {statusText}
            </Badge>
          </div>
        }
        icon={<FaUserFriends size={24} />}
        actions={
          <div className={styles.headerActions}>
            {canActivate && (
              <Button
                variant="success"
                onClick={() => setPendingStatus(1)}
                leftIcon={<FaIcons.FaCheckCircle />}
              >
                {translate('global.activateClient')}
              </Button>
            )}
            {canDeactivate && (
              <Button
                variant="danger"
                onClick={() => setPendingStatus(2)}
                leftIcon={<FaIcons.FaTimesCircle />}
              >
                {translate('global.desactivateClient')}
              </Button>
            )}
            <Button
              variant="primary" 
              onClick={handleBack}
              leftIcon={<FaIcons.FaArrowLeft />}
            >
              {translate('dashboard.clients.detail.actions.backToClients')}
            </Button>
          </div>
        }
        divider={false}
      />

      {/* Tabs */}
      <Tabs
        items={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        lazy={false}
      />

      <ModalContent
        title={translate('global.alert.attention')}
        body={
          <span>
            {pendingStatus === 1
              ? translate('dashboard.clients.activate.alert.message')
              : translate('dashboard.clients.desactivate.alert.message')}
          </span>
        }
        footer={
          <>
            <Button
              variant="ghostOutline"
              onClick={() => setPendingStatus(null)}
              disabled={updatingStatus}
            >
              {translate('global.cancel')}
            </Button>
            <Button
              variant={pendingStatus === 1 ? 'success' : 'danger'}
              onClick={handleConfirmStatusChange}
              disabled={updatingStatus}
              loading={updatingStatus}
            >
              {translate('global.accept')}
            </Button>
          </>
        }
        isOpenModal={pendingStatus !== null}
        onHide={() => !updatingStatus && setPendingStatus(null)}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </PageContainer>
  );
};

export default DetailsClients;
